import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Save, Package, Check, Wrench, AlertTriangle, Calendar, AlertCircle, Link2 } from 'lucide-react';
// FT-209: Using native buttons instead of Catalyst Button for proper form submission
import { Input } from '../../../catalyst/input';
import { Select } from '../../../catalyst/select';
import { Textarea } from '../../../catalyst/textarea';
import { Badge } from '../../../catalyst/badge';
import Dropdown from '../../../common/Dropdown';
import { MAINTENANCE_CATEGORIES } from '../../../../config/maintenanceRequirements';
import { generateSequentialId, ID_PREFIXES } from '../../../../utils/sequentialIdGenerator';
import { useVessel } from '../../../../contexts/VesselContext';
// FT-407: Equipment Register migrated to IndexedDB — read via the IDB-backed hook.
// readJSON() returned [] post-migration, hiding the FT-143 Link-to-Equipment picker here too.
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
import { useMaintenanceJobsDomain } from '../../../../hooks/domain/maintenance/useMaintenanceJobsDomain';
import { useDefects } from '../../../../hooks/domain/defects/useDefects';
import { convertIntervalToDays } from '../../../../utils/intervalHelpers';

// FT-48: Support editing existing jobs via initialData prop
// FT-143: Added equipment linking support for edit mode
// FT-209: Added linkedDefect prop for creating corrective jobs from defects
const AddCustomJobModal = ({ isOpen, onClose, onSave, initialData, linkedDefect, existingJobs }) => {
  // FT-209 FIX: read all jobs (incl. archived) directly so generateSequentialId never collides
  const { allJobs: allStoredJobs } = useMaintenanceJobsDomain();
  const isEditing = !!initialData;
  // FT-209: Corrective job mode - when creating from a defect
  const isCorrectiveJob = !!linkedDefect;
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();

  // FT-143: Load equipment register for linking
  // FT-407: source from the IDB-backed hook. `equipment` = non-archived combined register.
  const { equipment } = useEquipmentRegister();

  // FT-143: Equipment selection state - initialize from initialData when editing
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);

  // FT-353c: Reverse selector — pick an existing open defect to link this
  // new maintenance job to. Only relevant when the modal is opened from the
  // maintenance side (linkedDefect prop not supplied). When linkedDefect IS
  // supplied (FT-353b defect-side flow) the dropdown stays hidden and the
  // linking is handled by DefectsModule.handleGenerateCorrectiveJob to
  // avoid double-writes.
  const { getOpen: getOpenDefects, linkCorrectiveJob } = useDefects();
  const openDefects = useMemo(
    () => (isCorrectiveJob || isEditing ? [] : getOpenDefects()),
    // getOpenDefects reads live state; recompute per open cycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCorrectiveJob, isEditing, isOpen]
  );
  const showLinkDefectDropdown = !isCorrectiveJob && !isEditing && openDefects.length > 0;
  const [selectedLinkDefectId, setSelectedLinkDefectId] = useState('');

  const defaultFormData = {
    item: '',
    category: 'custom',
    interval: 30,
    unit: 'days',
    requirement: '',
    notes: '',
    assignedTo: '',  // FT-183: Optional planning field
    jobDescription: {
      purpose: '',
      scope: ''
    },
    // FT-241: First Due Date
    firstDueOption: 'one_cycle',  // 'one_cycle' | 'due_now' | 'custom'
    customFirstDueDate: ''
  };

  const [formData, setFormData] = useState(defaultFormData);

  // FT-48: Initialize form with initialData when editing
  // FT-186 FIX: Load from equipmentType OR item (UI prioritizes equipmentType)
  // FT-143: Also initialize linkedEquipmentIds when editing
  // FT-209: Pre-fill from linkedDefect when creating corrective job
  useEffect(() => {
    if (linkedDefect) {
      // FT-209: Pre-fill for corrective job from defect
      const defectDesc = linkedDefect.description || linkedDefect.title || 'Defect repair';
      setFormData({
        item: `Corrective: ${defectDesc.slice(0, 50)}${defectDesc.length > 50 ? '...' : ''}`,
        category: 'custom',
        interval: 0,  // Not used for one-time jobs
        unit: 'days', // Not used for one-time jobs
        requirement: 'Defect rectification',
        notes: `Linked to defect: ${linkedDefect.id}\n\nOriginal defect: ${defectDesc}`,
        assignedTo: '',  // FT-183: Include in corrective job path
        jobDescription: {
          purpose: `Rectify defect: ${defectDesc}`,
          scope: linkedDefect.corrective_action || ''
        }
      });
      setSelectedEquipmentIds([]);
    } else if (initialData) {
      setFormData({
        // FT-186: Try equipmentType first (what UI displays), fallback to item
        item: initialData.equipmentType || initialData.item || '',
        category: initialData.category || 'custom',
        interval: initialData.interval || 30,
        unit: initialData.unit || 'days',
        requirement: initialData.requirement || '',
        notes: initialData.notes || '',
        assignedTo: initialData.assignedTo || '',  // FT-183
        jobDescription: {
          purpose: initialData.jobDescription?.purpose || initialData.description?.purpose || '',
          scope: initialData.jobDescription?.scope || initialData.description?.scope || ''
        }
      });
      // FT-143: Initialize equipment selection from existing links
      setSelectedEquipmentIds(initialData.linkedEquipmentIds || []);
    } else {
      setFormData(defaultFormData);
      setSelectedEquipmentIds([]);
    }
    // FT-353c: Reset the reverse-selector each time the modal is (re)opened
    // for a fresh maintenance-side job.
    setSelectedLinkDefectId('');
  }, [initialData, linkedDefect]);

  const [errors, setErrors] = useState({});

  // FT-143: Toggle equipment selection
  const toggleEquipment = (equipmentId) => {
    setSelectedEquipmentIds(prev =>
      prev.includes(equipmentId)
        ? prev.filter(id => id !== equipmentId)
        : [...prev, equipmentId]
    );
  };

  // Format date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // FT-241: Calculate initialNextDue based on user selection
  const calculateInitialNextDue = () => {
    // Corrective jobs don't use initialNextDue
    if (isCorrectiveJob) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (formData.firstDueOption) {
      case 'due_now':
        return formatLocalDate(today);
      case 'custom':
        return formData.customFirstDueDate || null;
      case 'one_cycle':
      default:
        const intervalDays = convertIntervalToDays(parseInt(formData.interval), formData.unit);
        const dueDate = new Date(today);
        dueDate.setDate(dueDate.getDate() + intervalDays);
        return formatLocalDate(dueDate);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!formData.item.trim()) {
      newErrors.item = 'Item name is required';
    }
    // FT-209: Skip interval validation for corrective jobs (one-time)
    if (!isCorrectiveJob && (!formData.interval || formData.interval < 1)) {
      newErrors.interval = 'Valid interval is required';
    }
    // FT-241: Validate custom date if selected
    if (!isCorrectiveJob && !isEditing && formData.firstDueOption === 'custom' && !formData.customFirstDueDate) {
      newErrors.customFirstDueDate = 'Custom date is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // FT-353c: If the user picked an existing open defect from the reverse
    // selector, treat this as a corrective job for that defect too. The
    // linkedDefect prop path (FT-353b) still takes priority — if it's set
    // we've already stamped originatingDefectId above.
    const reverseLinkedDefectId =
      !isCorrectiveJob && !isEditing && selectedLinkDefectId
        ? selectedLinkDefectId
        : null;

    // FT-48: Create or update the custom job
    // FT-186 FIX: Sync equipmentType with item to ensure name persists correctly
    // The UI displays equipmentType || item, so both need to be updated
    // FT-143: Include linkedEquipmentIds for equipment linking
    // FT-209: Add corrective job fields when creating from defect
    const customJob = {
      // Preserve existing ID when editing, otherwise generate new one
      // FT-209 FIX: Use allStoredJobs (incl. archived) so generator never reuses an existing ID
      id: initialData?.id || generateSequentialId(allStoredJobs || existingJobs || [], ID_PREFIXES.MAINTENANCE),
      ...formData,
      // FT-186: Sync all name fields so list/detail/dashboard all show updated name
      name: formData.item,
      equipmentType: formData.item,
      interval: isCorrectiveJob ? 0 : parseInt(formData.interval),
      custom: true,
      // FT-143: Save linked equipment IDs
      linkedEquipmentIds: selectedEquipmentIds,
      // FT-241: Initial next due date for jobs with no completions (only for new jobs)
      ...(!isEditing && { initialNextDue: calculateInitialNextDue() }),
      // FT-209: Corrective job fields
      ...(isCorrectiveJob && {
        jobType: 'corrective',
        isOneTime: true,
        originatingDefectId: linkedDefect.id,
      }),
      // FT-353c: When user picks a defect via the reverse selector, stamp
      // the back-reference on the job so downstream views (FT-353d card
      // badge) can surface the linkage.
      ...(reverseLinkedDefectId && { originatingDefectId: reverseLinkedDefectId }),
      // Preserve original createdAt when editing, otherwise set new one
      createdAt: initialData?.createdAt || new Date().toISOString(),
      // Track updates
      updatedAt: isEditing ? new Date().toISOString() : undefined
    };

    onSave(customJob);

    // FT-353c: After job creation succeeds, write the back-reference on the
    // defect. Only for the maintenance-side path — the defect-side flow
    // (linkedDefect prop) is handled by DefectsModule to avoid double-calls.
    if (reverseLinkedDefectId) {
      linkCorrectiveJob(reverseLinkedDefectId, customJob.id);
    }

    // Reset form
    setFormData(defaultFormData);
    setSelectedEquipmentIds([]);  // FT-143: Reset equipment selection
    setSelectedLinkDefectId('');  // FT-353c: Reset reverse selector
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
          {/* Header - FT-48: Show different title when editing */}
          {/* FT-209: Show corrective job header when creating from defect */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-medium text-gray-900">
                {isCorrectiveJob ? 'Generate Corrective Job' : (isEditing ? 'Edit Maintenance Job' : 'Add Custom Maintenance Job')}
              </h3>
              {isCorrectiveJob && (
                <Badge color="orange">One-Time Corrective</Badge>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* FT-209: Linked defect banner */}
          {isCorrectiveJob && (
            <div className="mx-6 mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-orange-800">
                  Resolving Defect: {linkedDefect.id}
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  {linkedDefect.description || linkedDefect.title}
                </p>
              </div>
            </div>
          )}

          {/* Form - FT-209 fix: make form flex-1 with scrollable content */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* FT-353c: Reverse selector — link this maintenance job to
                  an existing open defect. Hidden when the modal was opened
                  from a defect (linkedDefect prop) or when there are no open
                  defects to link to. Rendered FIRST so the linkage decision
                  precedes the item/scope entry. */}
              {showLinkDefectDropdown && (
                <div data-testid="link-defect-section">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Link2 className="h-4 w-4 inline mr-1" />
                    Link to defect (optional)
                  </label>
                  <Dropdown
                    value={selectedLinkDefectId}
                    onChange={setSelectedLinkDefectId}
                    width="w-full"
                    options={[
                      { value: '', label: 'None (unlinked)' },
                      ...openDefects.map((d) => {
                        const label = (d.description || d.title || 'Defect').toString();
                        const truncated = label.length > 40 ? `${label.slice(0, 40)}...` : label;
                        return {
                          value: d.id,
                          label: `${d.id} — ${truncated}`,
                        };
                      }),
                    ]}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Attach this maintenance job to an existing open defect for traceability.
                  </p>
                </div>
              )}

              {/* Item Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Name *
                </label>
                <Input
                  value={formData.item}
                  onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                  placeholder="e.g., Engine Room Bilge Pump Test"
                  className={errors.item ? 'border-red-300' : ''}
                />
                {errors.item && (
                  <p className="mt-1 text-sm text-red-600">{errors.item}</p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="custom">Custom</option>
                  {Object.entries(MAINTENANCE_CATEGORIES).map(([key, value]) => (
                    <option key={key} value={value}>{key.replace(/_/g, ' ')}</option>
                  ))}
                </Select>
              </div>

              {/* FT-183: Assigned to - optional planning field */}
              <div>
                <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned to <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <Input
                  id="assignedTo"
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  placeholder="e.g. Chief Engineer, John Smith"
                />
              </div>

              {/* Frequency - FT-209: Hidden for corrective jobs (one-time) */}
              {!isCorrectiveJob && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interval *
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.interval}
                      onChange={(e) => setFormData({ ...formData, interval: e.target.value })}
                      className={errors.interval ? 'border-red-300' : ''}
                    />
                    {errors.interval && (
                      <p className="mt-1 text-sm text-red-600">{errors.interval}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit
                    </label>
                    <Select
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                      <option value="hours">Running Hours</option>
                    </Select>
                  </div>
                </div>
              )}

              {/* FT-241: First Due Date - Only show when adding new job (not editing or corrective) */}
              {!isCorrectiveJob && !isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    First Due Date
                  </label>
                  <div className="space-y-2">
                    {/* Due in one cycle (default) */}
                    <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${
                      formData.firstDueOption === 'one_cycle'
                        ? 'bg-cyan-50 border-cyan-200'
                        : 'hover:bg-gray-50 border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        name="firstDueOption"
                        value="one_cycle"
                        checked={formData.firstDueOption === 'one_cycle'}
                        onChange={(e) => setFormData({ ...formData, firstDueOption: e.target.value })}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">Due in one cycle</p>
                        <p className="text-xs text-gray-500">
                          First due in {formData.interval} {formData.unit} from today
                        </p>
                      </div>
                    </label>

                    {/* Due now */}
                    <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${
                      formData.firstDueOption === 'due_now'
                        ? 'bg-cyan-50 border-cyan-200'
                        : 'hover:bg-gray-50 border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        name="firstDueOption"
                        value="due_now"
                        checked={formData.firstDueOption === 'due_now'}
                        onChange={(e) => setFormData({ ...formData, firstDueOption: e.target.value })}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">Due now</p>
                        <p className="text-xs text-gray-500">Will show as "Due Soon" immediately</p>
                      </div>
                    </label>

                    {/* Custom date */}
                    <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${
                      formData.firstDueOption === 'custom'
                        ? 'bg-cyan-50 border-cyan-200'
                        : 'hover:bg-gray-50 border-gray-200'
                    }`}>
                      <input
                        type="radio"
                        name="firstDueOption"
                        value="custom"
                        checked={formData.firstDueOption === 'custom'}
                        onChange={(e) => setFormData({ ...formData, firstDueOption: e.target.value })}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">Custom date</p>
                      </div>
                    </label>

                    {/* Custom date picker */}
                    {formData.firstDueOption === 'custom' && (
                      <div className="ml-7 mt-1">
                        <input
                          type="date"
                          value={formData.customFirstDueDate}
                          onChange={(e) => setFormData({ ...formData, customFirstDueDate: e.target.value })}
                          className={`block w-full max-w-xs rounded-md border py-1.5 px-2 text-sm text-gray-900 ${
                            errors.customFirstDueDate ? 'border-red-300 focus:ring-red-600' : 'border-gray-300 focus:ring-cyan-600'
                          } focus:outline-none focus:ring-2`}
                        />
                        {errors.customFirstDueDate && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {errors.customFirstDueDate}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* FT-209: One-time job indicator for corrective jobs */}
              {isCorrectiveJob && (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    <span>This is a <strong>one-time corrective job</strong>. It will not recur after completion.</span>
                  </p>
                </div>
              )}

              {/* Requirement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requirement / Standard
                </label>
                <Input
                  value={formData.requirement}
                  onChange={(e) => setFormData({ ...formData, requirement: e.target.value })}
                  placeholder="e.g., Company procedure, Manufacturer recommendation"
                />
              </div>

              {/* Purpose */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose
                </label>
                <Input
                  value={formData.jobDescription.purpose}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    jobDescription: { ...formData.jobDescription, purpose: e.target.value }
                  })}
                  placeholder="Brief description of why this maintenance is needed"
                />
              </div>

              {/* Scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scope of Work
                </label>
                <Textarea
                  value={formData.jobDescription.scope}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    jobDescription: { ...formData.jobDescription, scope: e.target.value }
                  })}
                  placeholder="Enter tasks, one per line. Use • for bullet points"
                  rows={4}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Start each line with • for bullet points
                </p>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional information or special instructions"
                  rows={2}
                />
              </div>

              {/* FT-143: Equipment Linking Section */}
              {equipment.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Package className="h-4 w-4 inline mr-1" />
                    Link Equipment ({selectedEquipmentIds.length} selected)
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                    {equipment.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => toggleEquipment(item.id)}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                          selectedEquipmentIds.includes(item.id) ? 'bg-cyan-50' : ''
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                          selectedEquipmentIds.includes(item.id)
                            ? 'bg-cyan-600 border-cyan-600'
                            : 'border-gray-300'
                        }`}>
                          {selectedEquipmentIds.includes(item.id) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {item.name || item.equipmentName}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {item.domain || item.category} • {item.manufacturer || 'No manufacturer'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Click to select equipment items to link to this job
                  </p>
                </div>
              )}
            </div>

            {/* Footer - FT-209 fix: sticky footer outside scroll area */}
            {/* FT-209 fix: Use native buttons to ensure form submission works correctly */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 flex items-center"
              >
                {isEditing ? (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Job
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCustomJobModal;